import { Button, ButtonTypes, MoveableDialog } from "@repo/ui";
import { useMemo, useState } from "react";

import type { ApplicationSetting } from "../../../models";
import type { DefinitionTree, Segment } from "../settingsTree";

import { FLAG_TOOLTIPS, flagText } from "../settingFlags";
import { rowAction } from "../settingForm";
import { groupByName, settingRow, settingTypeLabel } from "../settingsTree";
import { OrphanedBadge } from "./orphanedBadge";
import { SegmentRowsGrid } from "./segmentRowsGrid";
import { SettingChain } from "./settingChain";
import { SourcePills } from "./sourcePills";
import style from "./style.module.scss";
import { ValueText } from "./valueText";

interface Props {
	tree: DefinitionTree;
	/** Every setting row, so what's shown follows saves and deletes made while the dialog is open */
	settings: ApplicationSetting[];
	/** The segment picked in the filter bar, so values and row statuses match the grid */
	segment?: Segment;
	/** The setting to show first */
	name: string;
	onClose: () => void;
	onEdit: (setting: ApplicationSetting) => void;
	onDelete: (setting: ApplicationSetting) => void;
	/** Opens the add setting dialog for the named setting and the picked segment */
	onAddOverride: (name: string) => void;
}

/** Wide enough for the value row and the rows sub-grid's columns */
const MIN_SIZE = { width: 600, height: 300 };

function companyWideText(isCompanyWide?: boolean | null) {
	if (isCompanyWide == null) return "Not sure";
	return isCompanyWide ? "Yes, the code reads it only company-wide" : "No, the code also reads it for a segment";
}

/**
 * Everything about one setting: what the grid shows, its definition's notes, its place in the hierarchy, and its rows.
 * Links open the setting a value comes from and the other levels of the hierarchy in place; Back returns
 */
export function SettingDetailsDialog({
	tree,
	settings,
	segment,
	name,
	onClose,
	onEdit,
	onDelete,
	onAddOverride,
}: Props) {
	// Every setting opened so far, the one shown last
	const [history, setHistory] = useState([name]);
	const current = history[history.length - 1];
	const settingsByName = useMemo(() => groupByName(settings), [settings]);
	const row = useMemo(
		() => settingRow(tree, settingsByName, current, undefined, segment),
		[tree, settingsByName, current, segment],
	);
	const definition = row.definitionName ? tree.nodes.get(row.definitionName)?.definition : undefined;
	const derivedFrom = row.source === "inherited" ? row.winner?.name : undefined;
	// When the value is the default, it's shown once, as the default value
	const isDefault = row.source === "default";
	const open = (next: string) => setHistory((opened) => [...opened, next]);

	return (
		<MoveableDialog
			title="Setting details"
			open
			onOpenChange={(isOpen) => !isOpen && onClose()}
			fitContent
			movable
			resizable
			closeOnEscape
			closeOnClickOutside
			minSize={MIN_SIZE}
			footer={
				<div className={style.dialogButtons}>
					{history.length > 1 && (
						<Button
							types={[ButtonTypes.Outline]}
							onClick={() => setHistory((opened) => opened.slice(0, -1))}
						>
							Back
						</Button>
					)}
					<Button types={[ButtonTypes.Primary]} onClick={onClose}>
						Close
					</Button>
				</div>
			}
		>
			<div className={style.detailsBody}>
				<div className={style.settingCell}>
					<span className={style.settingName}>{row.name}</span>
					{row.friendlyName && <span className={style.friendlyName}>{row.friendlyName}</span>}
					{row.isOrphaned && <OrphanedBadge />}
				</div>

				<dl className={style.valueSummary}>
					<div>
						<dt>{isDefault ? "Default value" : "Value"}</dt>
						<dd>
							<ValueText value={row.value} enumOptions={row.enumOptions} masked={row.winner?.isMasked} />
						</dd>
					</div>
					<div>
						<dt>Source</dt>
						<dd>
							<SourcePills row={row} />
						</dd>
					</div>
					{!isDefault && !row.isOrphaned && (
						<div>
							<dt>Default</dt>
							<dd>
								{row.defaultValue == null ? (
									"None"
								) : (
									<ValueText value={row.defaultValue} enumOptions={row.enumOptions} />
								)}
							</dd>
						</div>
					)}
				</dl>

				<dl className={style.detailsList}>
					{derivedFrom && (
						<>
							<dt>Derived from</dt>
							<dd>
								<Button
									types={[ButtonTypes.Text]}
									title="Show the setting this value comes from"
									onClick={() => open(derivedFrom)}
								>
									<span className={style.settingName}>{derivedFrom}</span>
								</Button>
							</dd>
						</>
					)}
					<dt>Type</dt>
					<dd>{settingTypeLabel(row.dataType)}</dd>
					{row.enumOptions?.length ? (
						<>
							<dt>Options</dt>
							<dd>{row.enumOptions.map((option) => option.label).join(", ")}</dd>
						</>
					) : null}
					{definition && (
						<>
							<dt>Company-wide</dt>
							<dd>{companyWideText(definition.isCompanyWide)}</dd>
							<dt className={style.flagLabel} title={FLAG_TOOLTIPS.secret}>
								Secret
							</dt>
							<dd>{flagText(definition.isSecret)}</dd>
							<dt className={style.flagLabel} title={FLAG_TOOLTIPS.required}>
								Required
							</dt>
							<dd>{flagText(definition.isRequired ?? false)}</dd>
							<dt className={style.flagLabel} title={FLAG_TOOLTIPS.cached}>
								Cached
							</dt>
							<dd>{flagText(definition.cached ?? false)}</dd>
						</>
					)}
					{row.description && (
						<>
							<dt>Description</dt>
							<dd>{row.description}</dd>
						</>
					)}
					{row.notes && (
						<>
							<dt>Notes</dt>
							<dd>{row.notes}</dd>
						</>
					)}
				</dl>

				<SettingChain tree={tree} settingsByName={settingsByName} row={row} segment={segment} onOpen={open} />

				<section className={style.detailsSection}>
					<div className={style.sectionHeader}>
						<h4 className={style.sectionTitle}>Rows ({row.rows.length})</h4>
						{rowAction(row) === "addOverride" && (
							<Button
								types={[ButtonTypes.Outline]}
								title="Add a row for this setting and the selected segment"
								onClick={() => onAddOverride(row.name)}
							>
								Add override
							</Button>
						)}
					</div>
					{row.rows.length ? (
						// A new grid per setting, since the grid keeps the columns it first rendered
						<SegmentRowsGrid
							key={row.key}
							row={row}
							segment={segment}
							onEdit={onEdit}
							onDelete={onDelete}
						/>
					) : (
						<p className={style.hint}>This setting has no rows of its own.</p>
					)}
				</section>
			</div>
		</MoveableDialog>
	);
}
